import { useState, useEffect } from 'react'
import { ArrowDownIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useLazyQuery } from '@apollo/client'
import { ADD_BOAT_ROUTE_TRIP, UPDATE_BOAT_ROUTE_TRIP } from 'graphql/route/mutations'
import { useFormik } from 'formik'
import Flatpickr from 'react-flatpickr'

import * as Yup from 'yup'
import { BoatRouteTripStatusDropdown } from 'components/custom/RouteTripStatusDropdown'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { GET_BOAT_ROUTE_DETAILS, GET_TRIP_INFO, GET_RETURN_POLICY } from 'graphql/route/queries'
import { GET_BOAT_DAY_PASS_PRICE } from 'graphql/booking/queries'
import { RoutesIcon } from 'components/icons'
import TripAlert from '../TripAlert'

export default function MetroTripForm() {
    const { route_id, trip_id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addTripErrorMsg, setAddTripErrorMsg] = useState(t('Problem Adding Route'))
    // Message
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [routeInfo, setRouteInfo] = useState({})
    const [tripInfo, setTripInfo] = useState(null)
    const [waypoints, setWaypoints] = useState([])
    const [returnPolicies, setReturnPolicies] = useState([])
    const [isInternationalRoute, setIsInternationalRoute] = useState(false)
    const [dayPassPrices, setDayPassPrices] = useState([])

    const [getRouteInfo, { loading: routeInfoLoading, error: routeInfoError }] = useLazyQuery(GET_BOAT_ROUTE_DETAILS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getBoatRoute.success && data.getBoatRoute.code === '200') {
                setRouteInfo(data.getBoatRoute.boat_routes[0])
                setWaypoints(data.getBoatRoute.boat_routes[0].master_route?.waypoints)
            }
        },
    })
    const [getTripInfo, { loading: tripInfoLoading, error: tripInfoError }] = useLazyQuery(GET_TRIP_INFO, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getBoatRouteTrip.success && data.getBoatRouteTrip.code === '200') {
                setTripInfo(data.getBoatRouteTrip.trip)
                setWaypoints(data.getBoatRouteTrip.trip.schedule)
                // Check if route is international
                const routeType = data.getBoatRouteTrip.trip?.route?.master_route?.route_type?.code
                setIsInternationalRoute(routeType === 'INL')
            }
        },
    })

    const [getReturnPolicies] = useLazyQuery(GET_RETURN_POLICY, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.returnPolicy) {
                setReturnPolicies(data.returnPolicy)
            }
        },
    })

    const [getDayPassPrices, { loading: dayPassLoading }] = useLazyQuery(GET_BOAT_DAY_PASS_PRICE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getBoatDayPassPrices?.success && data?.getBoatDayPassPrices?.day_pass_prices) {
                // Filter only active day pass prices
                const activePrices = data.getBoatDayPassPrices.day_pass_prices.filter(
                    price => price.day_pass_price_status?.code === 'ACT'
                )
                setDayPassPrices(activePrices)
            }
        },
        onError: error => {
            console.error('Error fetching day pass prices:', error)
            setDayPassPrices([])
        },
    })
    useEffect(() => {
        // Fetch return policies on mount
        getReturnPolicies({ variables: { code: null } })

        // Fetch day pass prices
        getDayPassPrices({
            variables: {
                page: { limit: 100, offset: 0 },
                priceFilter: null,
            },
        })

        if (trip_id == 0) {
            getBoatsRouteInfo()
        } else {
            getTripDetail()
        }
    }, [route_id, trip_id])
    // Search Route Info
    const getBoatsRouteInfo = () => {
        getRouteInfo({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                routeId: route_id,
                routeName: null,
            },
        })
    }
    const getTripDetail = () => {
        getTripInfo({
            variables: {
                tripId: trip_id,
            },
        })
    }

    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    // Create initial values for waypoints
    const initialWaypoints = waypoints.reduce((acc, wps, index) => {
        const isFirstWaypoint = index === 0
        const isLastWaypoint = index === waypoints.length - 1

        acc[`waypoint_${index}_startTime`] = wps.arrival_time || ''
        acc[`waypoint_${index}_endTime`] = wps.departure_time || ''
        acc[`waypoint_${index}_remarks`] = wps.remarks || ''

        // First and last waypoints: both checkboxes checked by default
        if (isFirstWaypoint || isLastWaypoint) {
            acc[`waypoint_${index}_entryAllowed`] = wps.entry_allowed !== undefined ? wps.entry_allowed : true
            acc[`waypoint_${index}_exitAllowed`] = wps.exit_allowed !== undefined ? wps.exit_allowed : true
            acc[`waypoint_${index}_stopChargeable`] = wps.stop_chargeable !== undefined ? wps.stop_chargeable : true
        } else {
            // Middle waypoints: use existing values or default to true
            acc[`waypoint_${index}_entryAllowed`] = wps.entry_allowed !== undefined ? wps.entry_allowed : true
            acc[`waypoint_${index}_exitAllowed`] = wps.exit_allowed !== undefined ? wps.exit_allowed : true
            acc[`waypoint_${index}_stopChargeable`] = wps.stop_chargeable !== undefined ? wps.stop_chargeable : true
        }

        return acc
    }, {})

    // Determine default values for daypass_allowed based on route type
    // For new trips: true for domestic, false for international
    // For existing trips: use the saved value
    // Charter routes should never require day pass selection
    const getDefaultDaypassAllowed = () => {
        if (routeInfo?.is_charter || tripInfo?.route?.is_charter) {
            return false
        }
        if (tripInfo?.daypass_allowed !== undefined) {
            return tripInfo.daypass_allowed
        }
        // Default: true for domestic, false for international
        return !isInternationalRoute
    }

    // Form Validation
    const RouteTripFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            tripNameEn: tripInfo?.trip_name?.en || '',
            tripNameAr: tripInfo?.trip_name?.ar || '',
            startTime: tripInfo?.start_time || '',
            tripEndTime: tripInfo?.end_time || '',
            validFrom: tripInfo?.valid_from || '',
            validTo: tripInfo?.valid_to || '',
            status: tripInfo?.status.code || '',
            stopsTime: waypoints.map(wp => wp.arrival_time),
            daypassAllowed: getDefaultDaypassAllowed(),
            dayPassMasterPriceId: tripInfo?.daypass_id || '',
            rescheduleAllowed: tripInfo?.reschedule_allowed !== undefined ? tripInfo.reschedule_allowed : true,
            isTripPrivate: tripInfo?.is_trip_private !== undefined ? tripInfo.is_trip_private : false,
            returnTripPolicy: tripInfo?.return_trip_policy?.code || '',
            ...initialWaypoints,
        },
        validationSchema: Yup.object().shape({
            tripNameEn: Yup.string().required(t('Trip Name is required')),
            daypassAllowed: Yup.boolean(),
            dayPassMasterPriceId: Yup.string().when('daypassAllowed', {
                is: true,
                then: schema => schema.required(t('Day Pass is required')),
                otherwise: schema => schema.notRequired(),
            }),
            tripNameAr: Yup.string().required(t('Arabic name is required')),
            returnTripPolicy: Yup.string().required(t('Refund Policy is required')),
            startTime: Yup.string().required(t('Start time is required')),
            tripEndTime: Yup.string().required(t('End time is required')),
            status: Yup.string().required(t('Status is required')),
            validFrom: Yup.string().required(t('Valid date is required')),
            // stopsTime: Yup.array().of(Yup.string().required(t('Stop Time is required'))),
            ...waypoints.reduce((acc, waypoint, index) => {
                acc[`waypoint_${index}_startTime`] = Yup.string().required(t('Arrival Time is required'))
                acc[`waypoint_${index}_endTime`] = Yup.string().required(t('Departure Time is required'))
                acc[`waypoint_${index}_remarks`] = Yup.string().required(t('Remarks is required'))
                acc[`waypoint_${index}_entryAllowed`] = Yup.boolean()
                acc[`waypoint_${index}_exitAllowed`] = Yup.boolean()
                acc[`waypoint_${index}_stopChargeable`] = Yup.boolean()
                return acc
            }, {}),
        }),
        onSubmit: values => {
            console.log('values===', values)
            setSubmitLoading(true)
            submitRoteTrip(values)
        },
        onErrors: errors => {
            console.log('errors===', errors)
        },
    })
    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }
    //  Mutation Add
    const [addBoatRouteTrip, { loading: addLoading }] = useMutation(ADD_BOAT_ROUTE_TRIP, {
        onCompleted: data => {
            if (data?.addBoatRouteTrip.code === '200' && data?.addBoatRouteTrip.success === true) {
                RouteTripFormValidation.setSubmitting(false)
                setSuccessMessage(data?.addBoatRouteTrip.message)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // // Time delay to redirect to the users page
                setTimeout(() => {
                    navigate(-1)
                }, 50)
            } else {
                RouteTripFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddTripErrorMsg(data?.addBoatRouteTrip.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            RouteTripFormValidation.setSubmitting(false)
            setAddTripErrorMsg(error.message)
            setShowErrDialog(true)
        },
    })

    // Mutation Update
    const [updateBoatRouteTrip, { loading: updateLoading }] = useMutation(UPDATE_BOAT_ROUTE_TRIP, {
        onCompleted: data => {
            if (data?.updateBoatRouteTrip.code === '200' && data?.updateBoatRouteTrip.success === true) {
                RouteTripFormValidation.setSubmitting(false)
                setSuccessMessage(data?.updateBoatRouteTrip.message)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    navigate(-1)
                }, 50)
            } else {
                RouteTripFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddTripErrorMsg(data?.updateBoatRouteTrip.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            console.log('error===', error)
            setSubmitLoading(false)
            RouteTripFormValidation.setSubmitting(false)
            setAddTripErrorMsg(error.message)
            setShowErrDialog(true)
        },
    })

    useEffect(() => {
        if (routeInfo?.is_charter || tripInfo?.route?.is_charter) {
            RouteTripFormValidation.setFieldValue('daypassAllowed', false)
            RouteTripFormValidation.setFieldValue('dayPassMasterPriceId', '')
        }
    }, [routeInfo?.is_charter, tripInfo?.route?.is_charter])
    
    const submitRoteTrip = async tripInfo => {
        console.log('tripInfo===')
        try {
            RouteTripFormValidation.setSubmitting(true)
            // Construct the formatted data
            const formattedData = {
                trip_name: {
                    en: tripInfo.tripNameEn,
                    ar: tripInfo.tripNameAr,
                },
                route_id: route_id,
                start_time: tripInfo.startTime,
                end_time: tripInfo.tripEndTime,
                status_code: tripInfo.status,
                schedule: waypoints.map((wp, index) => ({
                    location_id: wp.location.id,
                    status_code: 'ACT', // Assuming status code is always 'ACT'
                    stop_number: wp.stop_number,
                    arrival_time: tripInfo[`waypoint_${index}_startTime`],
                    departure_time: tripInfo[`waypoint_${index}_endTime`],
                    remarks: tripInfo[`waypoint_${index}_remarks`],
                    entry_allowed: tripInfo[`waypoint_${index}_entryAllowed`],
                    exit_allowed: tripInfo[`waypoint_${index}_exitAllowed`],
                    stop_chargeable: tripInfo[`waypoint_${index}_stopChargeable`],
                })),
                valid_from: tripInfo.validFrom === '' ? null : tripInfo.validFrom,
                valid_to: tripInfo.validTo === '' ? null : tripInfo.validTo,
                daypass_allowed: tripInfo.daypassAllowed,
                daypass_id: tripInfo.dayPassMasterPriceId || null,
                reschedule_allowed: tripInfo.rescheduleAllowed,
                is_trip_private: tripInfo.isTripPrivate,
                return_trip_policy: tripInfo.returnTripPolicy || null,
            }

            console.log('formattedData===', formattedData)
            if (trip_id && trip_id != 0) {
                updateBoatRouteTrip({
                    variables: {
                        boatRouteTrip: formattedData,
                        tripId: trip_id,
                    },
                })
            } else {
                addBoatRouteTrip({
                    variables: {
                        boatRouteTrip: formattedData,
                    },
                })
            }

            console.log('formattedData===', 'Done')
        } catch (error) {
            RouteTripFormValidation.setSubmitting(false)
            setSubmitLoading(false)
        }
    }

    return (
        <>
            <div>
                <div className="mb-0 flex justify-between items-center">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                        <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                <RoutesIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                            </div>
                            <div>
                                <span className="ms-3"> {t('Manage Schedule')}</span>
                                <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                    {t(' Plan your schedule')}.
                                </p>
                            </div>
                        </h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                        <button
                            type="button"
                            onClick={() => {
                                navigate(`/metro-routes/metro-route-details/${route_id}`)
                            }}
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={RouteTripFormValidation.isSubmitting}
                            type="button"
                            onClick={() => {
                                console.log('RouteTripFormValidation===', RouteTripFormValidation)
                                RouteTripFormValidation.handleSubmit()
                            }}
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
                <div className="mt-4">
                    <TripAlert />
                </div>
                <form className="mb-8 mt-10">
                    <div className="space-y-12">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Trip Details')}</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of the trip')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Trip Name English')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="tripNameEn"
                                        name="tripNameEn"
                                        type="text"
                                        value={RouteTripFormValidation.values.tripNameEn}
                                        placeholder="Enter here"
                                        onChange={RouteTripFormValidation.handleChange}
                                        onBlur={RouteTripFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {RouteTripFormValidation.touched.tripNameEn &&
                                        RouteTripFormValidation.errors.tripNameEn && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.tripNameEn}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Trip Name Arabic')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="tripNameAr"
                                        name="tripNameAr"
                                        type="text"
                                        value={RouteTripFormValidation.values.tripNameAr}
                                        placeholder="Enter here"
                                        onChange={RouteTripFormValidation.handleChange}
                                        onBlur={RouteTripFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {RouteTripFormValidation.touched.tripNameAr &&
                                        RouteTripFormValidation.errors.tripNameAr && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.tripNameAr}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="start-time"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Start Time')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="startTime"
                                        name="startTime"
                                        value={RouteTripFormValidation.values.startTime}
                                        placeholder="Choose here"
                                        onChange={RouteTripFormValidation.handleChange}
                                        onBlur={RouteTripFormValidation.handleBlur}
                                        type="time"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {RouteTripFormValidation.touched.startTime &&
                                        RouteTripFormValidation.errors.startTime && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.startTime}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="end-time"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('End Time')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="tripEndTime"
                                        name="tripEndTime"
                                        value={RouteTripFormValidation.values.tripEndTime}
                                        placeholder="Choose here"
                                        onChange={RouteTripFormValidation.handleChange}
                                        onBlur={RouteTripFormValidation.handleBlur}
                                        type="time"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {RouteTripFormValidation.touched.tripEndTime &&
                                        RouteTripFormValidation.errors.tripEndTime && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.tripEndTime}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="status"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    {/* <BoatRouteTripStatusDropdown
                                        fields={{
                                            name: 'status',
                                            id: 'status',
                                            value: RouteTripFormValidation.values.status,
                                            onChange: RouteTripFormValidation.handleChange,
                                        }}
                                        error={
                                            RouteTripFormValidation.touched.status &&
                                            !!RouteTripFormValidation.errors.status
                                        }
                                        helperText={
                                            RouteTripFormValidation.touched.status
                                                ? RouteTripFormValidation.errors.status
                                                : ''
                                        }
                                        purpose="add"
                                    /> */}
                                    <select
                                        id="status"
                                        name="status"
                                        value={RouteTripFormValidation.values.status}
                                        onChange={RouteTripFormValidation.handleChange}
                                        onBlur={RouteTripFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    >
                                        <option value="">{t('Select Status')}</option>
                                        <option value="ACT">{t('Active')}</option>
                                        <option value="INA">{t('Inactive')}</option>
                                    </select>

                                    {RouteTripFormValidation.touched.status &&
                                        RouteTripFormValidation.errors.status && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.status}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="validFrom"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Valid From')}
                                        {/* <span className="text-red">*</span> */}
                                    </label>
                                    <Flatpickr
                                        id="validFrom"
                                        name="validFrom"
                                        placeholder="yyyy-mm-dd"
                                        value={RouteTripFormValidation.values.validFrom}
                                        onChange={date =>
                                            RouteTripFormValidation.setFieldValue(
                                                'validFrom',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            dateFormat: 'Y/m/d', // Date format to display as 'YYYY/MM/DD'
                                            maxDate: '2050-12-31',
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            minDate:
                                                trip_id != null &&
                                                new Date(RouteTripFormValidation.values.validFrom) <= new Date()
                                                    ? RouteTripFormValidation.values.validFrom
                                                    : 'today',
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {RouteTripFormValidation.touched.validFrom &&
                                        RouteTripFormValidation.errors.validFrom && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.validFrom}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="validTo"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Valid To')}
                                        {/* <span className="text-red">*</span> */}
                                    </label>
                                    <Flatpickr
                                        id="validTo"
                                        name="validTo"
                                        placeholder="yyyy-mm-dd"
                                        value={RouteTripFormValidation.values.validTo}
                                        onChange={date =>
                                            RouteTripFormValidation.setFieldValue(
                                                'validTo',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            dateFormat: 'Y/m/d', // Date format to display as 'YYYY/MM/DD'
                                            maxDate: '2050-12-31',
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            minDate:
                                                trip_id != null &&
                                                new Date(RouteTripFormValidation.values.validTo) <= new Date()
                                                    ? RouteTripFormValidation.values.validTo
                                                    : 'today',
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {RouteTripFormValidation.touched.validTo &&
                                        RouteTripFormValidation.errors.validTo && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.validTo}
                                            </p>
                                        )}
                                </div>

                                {/* Trip Policy Settings */}
                                <div className="sm:col-span-full border-t border-gray-200 pt-4 mt-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-3">
                                        {t('Trip Policy Settings')}
                                    </label>
                                    <div className="flex flex-wrap gap-6">
                                        {!routeInfo?.is_charter && !tripInfo?.route?.is_charter && (
                                            <div className="flex items-center">
                                                <input
                                                    id="daypassAllowed"
                                                    name="daypassAllowed"
                                                    type="checkbox"
                                                    checked={RouteTripFormValidation.values.daypassAllowed || false}
                                                    onChange={e => {
                                                        RouteTripFormValidation.setFieldValue(
                                                            'daypassAllowed',
                                                            e.target.checked
                                                        )
                                                        // Clear day pass selection when unchecked
                                                        if (!e.target.checked) {
                                                            RouteTripFormValidation.setFieldValue(
                                                                'dayPassMasterPriceId',
                                                                ''
                                                            )
                                                        }
                                                    }}
                                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                <label
                                                    htmlFor="daypassAllowed"
                                                    className="ml-2 block text-sm text-gray-900"
                                                >
                                                    {t('Day Pass Allowed')}
                                                </label>
                                            </div>
                                        )}
                                        <div className="flex items-center">
                                            <input
                                                id="rescheduleAllowed"
                                                name="rescheduleAllowed"
                                                type="checkbox"
                                                checked={RouteTripFormValidation.values.rescheduleAllowed || false}
                                                onChange={e => {
                                                    RouteTripFormValidation.setFieldValue(
                                                        'rescheduleAllowed',
                                                        e.target.checked
                                                    )
                                                }}
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <label
                                                htmlFor="rescheduleAllowed"
                                                className="ml-2 block text-sm text-gray-900"
                                            >
                                                {t('Reschedule Allowed')}
                                            </label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                id="isTripPrivate"
                                                name="isTripPrivate"
                                                type="checkbox"
                                                checked={RouteTripFormValidation.values.isTripPrivate || false}
                                                onChange={e => {
                                                    RouteTripFormValidation.setFieldValue(
                                                        'isTripPrivate',
                                                        e.target.checked
                                                    )
                                                }}
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <label htmlFor="isTripPrivate" className="ml-2 block text-sm text-gray-900">
                                                {t('Private Trip')}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Day Pass Dropdown - Only show when daypass is checked */}
                                {!routeInfo?.is_charter && RouteTripFormValidation.values.daypassAllowed && !tripInfo?.route?.is_charter && (
                                    <div className="sm:col-span-3">
                                        <label
                                            htmlFor="dayPassMasterPriceId"
                                            className="block text-sm font-medium leading-6 text-gray-900"
                                        >
                                            {t('Day Pass')}
                                        </label>
                                        <select
                                            id="dayPassMasterPriceId"
                                            name="dayPassMasterPriceId"
                                            value={RouteTripFormValidation.values.dayPassMasterPriceId}
                                            onChange={RouteTripFormValidation.handleChange}
                                            onBlur={RouteTripFormValidation.handleBlur}
                                            disabled={dayPassLoading}
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <option value="">
                                                {dayPassLoading ? t('Loading...') : t('Select Day Pass')}
                                            </option>
                                            {dayPassPrices
                                                .filter(value => {
                                                    // Filter day passes to show only valid ones
                                                    /* Show type of "value" for my sanity
                                                    {
                                                        day_pass_price_status: { code: string, description: { en: string, ar: string } },
                                                        description: string | null,
                                                        is_editable: bool,
                                                        master_price_id: string,
                                                        name: string | null,
                                                        price: {
                                                            price: { ADL: number, CHD: number, INF: number, SNR: number },
                                                            price_id: string,
                                                            seat_type: { code: string, description: { en: string, ar: string } }
                                                        }[],
                                                        valid_from: string,
                                                        valid_to: string
                                                    }
                                                */

                                                    const today = new Date(Date.now())
                                                    const validTo = new Date(value.valid_to)
                                                    // console.log({today: today, validTo: validTo, isValidDayPass: today <= validTo, dayPassData: value});
                                                    return today <= validTo
                                                })
                                                .map(dayPass => (
                                                    <option
                                                        key={dayPass.master_price_id}
                                                        value={dayPass.master_price_id}
                                                    >
                                                        {dayPass.name?.en ||
                                                            dayPass.name?.ar ||
                                                            dayPass.master_price_id}
                                                    </option>
                                                ))}
                                        </select>
                                        {RouteTripFormValidation.touched.dayPassMasterPriceId &&
                                            RouteTripFormValidation.errors.dayPassMasterPriceId && (
                                                <p className="text-error text-xs mt-1">
                                                    {RouteTripFormValidation.errors.dayPassMasterPriceId}
                                                </p>
                                            )}
                                    </div>
                                )}

                                <div className="sm:col-span-3">
                                    <label
                                        htmlFor="returnTripPolicy"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Return Trip Policy')}
                                    </label>
                                    <select
                                        id="returnTripPolicy"
                                        name="returnTripPolicy"
                                        value={RouteTripFormValidation.values.returnTripPolicy}
                                        onChange={RouteTripFormValidation.handleChange}
                                        onBlur={RouteTripFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    >
                                        <option value="">{t('Select Return Trip Policy')}</option>
                                        {returnPolicies.map(policy => (
                                            <option key={policy.code} value={policy.code}>
                                                {policy.description?.en || policy.code}
                                            </option>
                                        ))}
                                    </select>
                                    {RouteTripFormValidation.touched.returnTripPolicy &&
                                        RouteTripFormValidation.errors.returnTripPolicy && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteTripFormValidation.errors.returnTripPolicy}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <form className="mb-8">
                    <div className="space-y-12">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Waypoints Details')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of waypoints arival and departure timings')}
                                </p>
                            </div>
                            {routeInfoLoading ||
                                (tripInfoLoading && (
                                    <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                                        {/* <img alt="logo" src={loadingImage} className="w-[250px]" /> */}
                                        <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                                    </div>
                                ))}
                            {routeInfoError ||
                                (routeInfoError && (
                                    <div className="flex justify-center items-center bg-white py-5">
                                        <div className="text-lg text-center">{t('Failed to fetch route!')}</div>
                                    </div>
                                ))}
                            {(!routeInfoLoading && !routeInfoError) ||
                                (!tripInfoLoading && !tripInfoError && waypoints?.length === 0 && (
                                    <div className="flex justify-center items-center bg-white py-5">
                                        <div className="text-lg text-center">{t('No route found!')}</div>
                                    </div>
                                ))}
                            {!routeInfoLoading && !routeInfoError && waypoints?.length > 0 && (
                                <div className="grid max-w-2xl grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                    {waypoints.map((wp, index) => (
                                        <>
                                            <div
                                                className="sm:col-span-full grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-6"
                                                key={waypoints[index].id}
                                            >
                                                <div className="sm:col-span-full flex gap-1 items-center">
                                                    <span className="circle-background bg-primary text-white">
                                                        {wp.stop_number}
                                                    </span>
                                                    <div>
                                                        <p className="text-xs">
                                                            {index === 0
                                                                ? 'Trip Start'
                                                                : index === waypoints.length - 1
                                                                  ? 'Trip End'
                                                                  : 'Stop'}
                                                        </p>
                                                        <h2 className="sm:col-span-full text-base font-semibold text-gray-900">
                                                            {wp.location.name.en}
                                                        </h2>
                                                    </div>
                                                </div>
                                                <div className="sm:col-span-3">
                                                    <label
                                                        htmlFor={`waypoint_${index}_startTime`}
                                                        className="block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        {t('Arrival Time')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <input
                                                        id={`waypoint_${index}_startTime`}
                                                        name={`waypoint_${index}_startTime`}
                                                        value={
                                                            RouteTripFormValidation.values[
                                                                `waypoint_${index}_startTime`
                                                            ]
                                                        }
                                                        placeholder="Choose here"
                                                        onChange={RouteTripFormValidation.handleChange}
                                                        onBlur={RouteTripFormValidation.handleBlur}
                                                        type="time"
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {RouteTripFormValidation.touched[`waypoint_${index}_startTime`] &&
                                                        RouteTripFormValidation.errors[
                                                            `waypoint_${index}_startTime`
                                                        ] && (
                                                            <p className="text-error text-xs mt-1">
                                                                {
                                                                    RouteTripFormValidation.errors[
                                                                        `waypoint_${index}_startTime`
                                                                    ]
                                                                }
                                                            </p>
                                                        )}
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label
                                                        htmlFor={`waypoint_${index}_endTime`}
                                                        className="block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        {t('Departure Time')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <input
                                                        id={`waypoint_${index}_endTime`}
                                                        name={`waypoint_${index}_endTime`}
                                                        value={
                                                            RouteTripFormValidation.values[`waypoint_${index}_endTime`]
                                                        }
                                                        placeholder="Choose here"
                                                        onChange={RouteTripFormValidation.handleChange}
                                                        onBlur={RouteTripFormValidation.handleBlur}
                                                        type="time"
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {RouteTripFormValidation.touched[`waypoint_${index}_endTime`] &&
                                                        RouteTripFormValidation.errors[`waypoint_${index}_endTime`] && (
                                                            <p className="text-error text-xs mt-1">
                                                                {
                                                                    RouteTripFormValidation.errors[
                                                                        `waypoint_${index}_endTime`
                                                                    ]
                                                                }
                                                            </p>
                                                        )}
                                                </div>
                                                <div className="sm:col-span-full">
                                                    <label
                                                        htmlFor={`waypoint_${index}_remarks`}
                                                        className="block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        {t('Remarks')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <textarea
                                                        id={`waypoint_${index}_remarks`}
                                                        name={`waypoint_${index}_remarks`}
                                                        rows="4"
                                                        value={
                                                            RouteTripFormValidation.values[`waypoint_${index}_remarks`]
                                                        }
                                                        placeholder="Enter here"
                                                        onChange={RouteTripFormValidation.handleChange}
                                                        onBlur={RouteTripFormValidation.handleBlur}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {RouteTripFormValidation.touched[`waypoint_${index}_remarks`] &&
                                                        RouteTripFormValidation.errors[`waypoint_${index}_remarks`] && (
                                                            <p className="text-error text-xs mt-1">
                                                                {
                                                                    RouteTripFormValidation.errors[
                                                                        `waypoint_${index}_remarks`
                                                                    ]
                                                                }
                                                            </p>
                                                        )}
                                                </div>
                                                <div className="sm:col-span-full">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                                        {t('Waypoint Restrictions')}
                                                    </label>
                                                    <div className="flex gap-6">
                                                        <div className="flex items-center">
                                                            <input
                                                                id={`waypoint_${index}_entryAllowed`}
                                                                name={`waypoint_${index}_entryAllowed`}
                                                                type="checkbox"
                                                                checked={
                                                                    RouteTripFormValidation.values[
                                                                        `waypoint_${index}_entryAllowed`
                                                                    ] || false
                                                                }
                                                                onChange={e => {
                                                                    RouteTripFormValidation.setFieldValue(
                                                                        `waypoint_${index}_entryAllowed`,
                                                                        e.target.checked
                                                                    )
                                                                }}
                                                                disabled={index === 0 || index === waypoints.length - 1}
                                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                            <label
                                                                htmlFor={`waypoint_${index}_entryAllowed`}
                                                                className={`ml-2 block text-sm ${
                                                                    index === 0 || index === waypoints.length - 1
                                                                        ? 'text-gray-500'
                                                                        : 'text-gray-900'
                                                                }`}
                                                            >
                                                                {t('Pickup Point')}
                                                            </label>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <input
                                                                id={`waypoint_${index}_exitAllowed`}
                                                                name={`waypoint_${index}_exitAllowed`}
                                                                type="checkbox"
                                                                checked={
                                                                    RouteTripFormValidation.values[
                                                                        `waypoint_${index}_exitAllowed`
                                                                    ] || false
                                                                }
                                                                onChange={e => {
                                                                    RouteTripFormValidation.setFieldValue(
                                                                        `waypoint_${index}_exitAllowed`,
                                                                        e.target.checked
                                                                    )
                                                                }}
                                                                disabled={index === 0 || index === waypoints.length - 1}
                                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                            <label
                                                                htmlFor={`waypoint_${index}_exitAllowed`}
                                                                className={`ml-2 block text-sm ${
                                                                    index === 0 || index === waypoints.length - 1
                                                                        ? 'text-gray-500'
                                                                        : 'text-gray-900'
                                                                }`}
                                                            >
                                                                {t('Drop Point')}
                                                            </label>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <input
                                                                id={`waypoint_${index}_stopChargeable`}
                                                                name={`waypoint_${index}_stopChargeable`}
                                                                type="checkbox"
                                                                checked={
                                                                    RouteTripFormValidation.values[
                                                                        `waypoint_${index}_stopChargeable`
                                                                    ] || false
                                                                }
                                                                onChange={e => {
                                                                    RouteTripFormValidation.setFieldValue(
                                                                        `waypoint_${index}_stopChargeable`,
                                                                        e.target.checked
                                                                    )
                                                                }}
                                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                            />
                                                            <label
                                                                htmlFor={`waypoint_${index}_stopChargeable`}
                                                                className="ml-2 block text-sm text-gray-900"
                                                            >
                                                                {t('Stop Chargeable')}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {index !== waypoints.length - 1 && (
                                                <div className="relative sm:col-span-full">
                                                    <div
                                                        aria-hidden="true"
                                                        className="absolute inset-0 flex items-center"
                                                    >
                                                        <div className="w-full border-t border-gray-200" />
                                                    </div>
                                                    <div className="relative flex justify-center text-sm font-medium leading-6">
                                                        <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full bg-white text-primary">
                                                            <ArrowDownIcon
                                                                aria-hidden="true"
                                                                className="-ml-0.5 h-5 w-5"
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </form>
                <div className="mb-8 flex justify-end items-center">
                    <div className="flex gap-3">
                        <a
                            type="button"
                            href={`/metro-routes/metro-route-details/${route_id}`}
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </a>
                        <button
                            disabled={RouteTripFormValidation.isSubmitting}
                            type="button"
                            onClick={() => {
                                console.log('RouteTripFormValidation===', RouteTripFormValidation)
                                RouteTripFormValidation.handleSubmit()
                            }}
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </div>
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title="Error"
                    message={addTripErrorMsg}
                    onClose={handleErrClose}
                    isMentee={false}
                />
            )}

            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleSucClose} />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
